import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./hooks/useTheme";
import HomePage from "./pages/HomePage";
import { DripleeLoader } from "./components/DripleeLoader";
import { Toaster } from "sonner";

// Lazy-load secondary routes to keep initial bundle lightweight (~40 KiB) and eliminate render blocking
const LoginPage = lazy(() => import("./pages/LoginPage"));
const CalculatorPage = lazy(() => import("./pages/CalculatorPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const InstallationPage = lazy(() => import("./pages/InstallationPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const DocsPage = lazy(() => import("./pages/DocsPage"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading && !user) {
    return <DripleeLoader message="Verifying Clinician Auth..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading && !user) {
    return <DripleeLoader message="Verifying Admin Auth..." />;
  }

  if (!user || user.role !== "admin") {
    return <AdminLoginPage />;
  }

  return children;
}

export function PublicRoute({ children }) {
  const { user } = useAuth();

  if (user) {
    return <Navigate to="/calculator" replace />;
  }

  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<DripleeLoader message="Preparing ICU workspace..." />}>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <HomePage />
                  </PublicRoute>
                }
              />
              <Route
                path="/calculator"
                element={
                  <ProtectedRoute>
                    <CalculatorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/methodology" element={<MethodologyPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/install" element={<InstallationPage />} />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

